/**
 * @fileoverview Search Context for managing global search functionality and recent searches.
 *
 * This context provides comprehensive search functionality including:
 * - Global search across multiple entity types (clients, users, time entries, invoices, activities)
 * - Search modal management and state
 * - Recent searches tracking with localStorage persistence
 * - Debounced search with configurable delay
 * - Search result highlighting and formatting
 * - Quick search shortcuts for specific entity types
 * - Search type filtering and categorization
 *
 * Used throughout the application for:
 * - Global search modal/interface
 * - Quick entity lookup and navigation
 * - Recent searches in search dropdown
 * - Search result highlighting in UI
 * - Keyboard shortcuts for search
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

/**
 * React Context for search functionality and state management
 * @type {React.Context<SearchContextValue>}
 */
const SearchContext = createContext();

/**
 * Custom hook to access the search context.
 * Must be used within a SearchProvider component.
 *
 * @function useSearch
 * @returns {SearchContextValue} The search context value containing search state and methods
 * @throws {Error} When used outside of SearchProvider
 *
 * @example
 * ```jsx
 * function SearchModal() {
 *   const {
 *     isSearchOpen,
 *     searchQuery,
 *     searchResults,
 *     setSearchQuery,
 *     closeSearch,
 *     handleResultSelect
 *   } = useSearch();
 *
 *   return (
 *     <Modal open={isSearchOpen} onClose={closeSearch}>
 *       <input
 *         value={searchQuery}
 *         onChange={(e) => setSearchQuery(e.target.value)}
 *         placeholder="Search..."
 *       />
 *       {searchResults.clients.map(client => (
 *         <div key={client.id} onClick={() => handleResultSelect(client)}>
 *           {client.name}
 *         </div>
 *       ))}
 *     </Modal>
 *   );
 * }
 * ```
 */
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

/**
 * Search Provider component that manages search state and provides search functionality.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to search context
 * @returns {JSX.Element} Provider component wrapping children with search context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <SearchProvider>
 *         <Router>
 *           <Routes>
 *             <Route path="/dashboard" element={<Dashboard />} />
 *           </Routes>
 *           <GlobalSearchModal />
 *         </Router>
 *       </SearchProvider>
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export const SearchProvider = ({ children }) => {
  const { user, APP_URL } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    clients: [],
    users: [],
    timeEntries: [],
    invoices: [],
    activities: [],
    projects: [],
    guide: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('clockwise-recent-searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading recent searches:', error);
        }
      }
    }
  }, []);

  /**
   * Saves a recent search to localStorage with deduplication
   * @param {string} query - The search query
   * @param {string} type - The search type or entity type
   * @param {Object} result - The search result object
   */
  const saveRecentSearch = useCallback((query, type, result) => {
    if (!query.trim()) return;

    const newSearch = {
      id: Date.now(),
      query: query.trim(),
      type,
      result,
      timestamp: new Date().toISOString()
    };

    setRecentSearches(prev => {
      const filtered = prev.filter(search =>
        search.query !== newSearch.query || search.type !== newSearch.type
      );
      const updated = [newSearch, ...filtered].slice(0, 20); // Keep only 20 recent searches
      localStorage.setItem('clockwise-recent-searches', JSON.stringify(updated));
      return updated;
    });
  }, []);

  /**
   * Performs a global search across multiple entity types
   * @param {string} query - The search query
   * @param {string} [type='all'] - The search type filter ('all', 'clients', 'users', etc.)
   */
  const performSearch = useCallback(async (query, type = 'all') => {
    if (!query.trim() || !user) {
      setSearchResults({
        clients: [],
        users: [],
        timeEntries: [],
        invoices: [],
        activities: [],
        projects: [],
        guide: []
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${APP_URL || ''}/api/search?q=${encodeURIComponent(query)}&type=${type}&limit=50&per_category=10`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        setSearchResults({
          clients: data.results?.clients || [],
          users: data.results?.users || [],
          timeEntries: data.results?.timeEntries || [],
          invoices: data.results?.invoices || [],
          activities: data.results?.activities || [],
          projects: [],
          guide: data.results?.guide || []
        });
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('Search failed:', response.status);
        }
        setSearchResults({
          clients: [],
          users: [],
          timeEntries: [],
          invoices: [],
          activities: [],
          projects: [],
          guide: []
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Search error:', error);
      }
      setSearchResults({
        clients: [],
        users: [],
        timeEntries: [],
        invoices: [],
        activities: [],
        projects: [],
        guide: []
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, APP_URL]);


  // Add search type state
  const [searchType, setSearchType] = useState('all');

  // Debounced search - always fetch all results, filter client-side by searchType
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery) {
        performSearch(searchQuery, 'all'); // Always fetch all categories
      }
    }, 500); // Increased from 300ms to 500ms to reduce rate limit hits

    return () => clearTimeout(delayedSearch);
  }, [searchQuery, performSearch]); // Don't include searchType - tabs filter client-side

  /** Opens the search modal and resets search query */
  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    setSearchQuery('');
  }, []);

  /** Closes the search modal and resets all search state */
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchType('all');
    setSearchResults({
      clients: [],
      users: [],
      timeEntries: [],
      invoices: [],
      activities: [],
      projects: [],
      guide: []
    });
  }, []);

  /**
   * Gets the total number of search results across all categories
   * @returns {number} Total number of search results
   */
  const getTotalResults = useCallback(() => {
    return Object.values(searchResults).reduce((total, results) => total + results.length, 0);
  }, [searchResults]);

  /**
   * Handles selection of a search result
   * @param {Object} result - The selected search result
   */
  const handleResultSelect = useCallback((result) => {
    saveRecentSearch(searchQuery, result.type, result);
    closeSearch();
    // Navigation will be handled by the component
  }, [searchQuery, saveRecentSearch, closeSearch]);

  /**
   * Quick search function for clients with prefilled query
   * @param {string} query - The search query for clients
   */
  const quickSearchClients = useCallback((query) => {
    setSearchQuery(`clients: ${query}`);
    openSearch();
  }, [openSearch]);

  /**
   * Quick search function for time entries with prefilled query
   * @param {string} query - The search query for time entries
   */
  const quickSearchTimeEntries = useCallback((query) => {
    setSearchQuery(`time: ${query}`);
    openSearch();
  }, [openSearch]);

  /**
   * Quick search function for invoices with prefilled query
   * @param {string} query - The search query for invoices
   */
  const quickSearchInvoices = useCallback((query) => {
    setSearchQuery(`invoices: ${query}`);
    openSearch();
  }, [openSearch]);

  /**
   * Quick search function for user guide with prefilled query
   * @param {string} query - The search query for user guide
   */
  const quickSearchGuide = useCallback((query) => {
    setSearchQuery(`guide: ${query}`);
    setSearchType('guide');
    openSearch();
  }, [openSearch]);

  /**
   * Highlights search query text within result text
   * @param {string} text - The text to highlight within
   * @param {string} query - The search query to highlight
   * @returns {string} Text with highlighted search terms wrapped in HTML spans
   */
  const highlightText = useCallback((text, query) => {
    if (!query || !text) return text;

    try {
      // Escape all special regex characters including backslashes
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return text.replace(regex, '<span class="search-highlight">$1</span>');
    } catch (error) {
      // If regex fails, return original text
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to highlight text:', error);
      }
      return text;
    }
  }, []);

  /**
   * The context value object containing all search state and methods
   * @typedef {Object} SearchContextValue
   * @property {boolean} isSearchOpen - Whether search modal is open
   * @property {string} searchQuery - Current search query
   * @property {Object} searchResults - Search results grouped by entity type
   * @property {Array} searchResults.clients - Client search results
   * @property {Array} searchResults.users - User search results
   * @property {Array} searchResults.timeEntries - Time entry search results
   * @property {Array} searchResults.invoices - Invoice search results
   * @property {Array} searchResults.activities - Activity search results
   * @property {Array} searchResults.projects - Project search results
   * @property {boolean} isLoading - Whether search is in progress
   * @property {Array} recentSearches - Recent search history
   * @property {string} searchType - Current search type filter
   * @property {Function} openSearch - Open search modal
   * @property {Function} closeSearch - Close search modal and reset state
   * @property {Function} setSearchQuery - Set current search query
   * @property {Function} setSearchType - Set search type filter
   * @property {Function} performSearch - Perform search API call
   * @property {Function} handleResultSelect - Handle search result selection
   * @property {Function} saveRecentSearch - Save search to recent history
   * @property {Function} quickSearchClients - Quick search for clients
   * @property {Function} quickSearchTimeEntries - Quick search for time entries
   * @property {Function} quickSearchInvoices - Quick search for invoices
   * @property {Function} getTotalResults - Get total search result count
   * @property {Function} highlightText - Highlight search terms in text
   */
  const value = {
    // State
    isSearchOpen,
    searchQuery,
    searchResults,
    isLoading,
    recentSearches,
    searchType,

    // Actions
    openSearch,
    closeSearch,
    setSearchQuery,
    setSearchType,
    performSearch,
    handleResultSelect,
    saveRecentSearch,

    // Quick search
    quickSearchClients,
    quickSearchTimeEntries,
    quickSearchInvoices,
    quickSearchGuide,

    // Utilities
    getTotalResults,
    highlightText
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};